import React, { useState, useEffect } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { devError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  useDocumentMeta({
    title: "Contact Us — ProsoftMarketplace",
    description:
      "Reach the ProsoftMarketplace team: +1 (888) 655-4464 · support@prosoftmarketplace.com · 2118 Glenridge Ct, Marietta, GA 30062.",
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Fire Google Ads conversion event once, when the contact form has been submitted successfully.
  useEffect(() => {
    if (!sent) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18232151665/4lRlCMSJ5s8cEPGc4vVD",
    });
  }, [sent]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received! We'll reply within 1 hour.");
    } catch (err) {
      devError("Contact form submit failed:", err);
      toast.error("Couldn't send. Please try email or phone instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contact</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Get in touch.
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            Questions about a license, refunds, bulk pricing or just shopping advice — we&apos;re happy to help.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:px-8">
        {/* Left: contact details */}
        <div className="lg:col-span-5">
          <div className="border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Reach us</div>
            <ul className="mt-4 space-y-5">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-emerald-500">
                  <Phone className="h-4 w-4 text-white" />
                </span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</div>
                  <a
                    href="tel:+18886554464"
                    data-testid="contact-phone"
                    className="font-display text-lg font-bold tracking-tight text-slate-900 hover:text-emerald-600"
                  >
                    +1 (888) 655-4464
                  </a>
                  <div className="text-xs text-slate-500">Mon–Sat, 8am–8pm ET</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-emerald-500">
                  <Mail className="h-4 w-4 text-white" />
                </span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</div>
                  <a
                    href="mailto:support@prosoftmarketplace.com"
                    data-testid="contact-email"
                    className="font-display text-lg font-bold tracking-tight text-slate-900 hover:text-emerald-600"
                  >
                    support@prosoftmarketplace.com
                  </a>
                  <div className="text-xs text-slate-500">Reply within 1 hour</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-emerald-500">
                  <MapPin className="h-4 w-4 text-white" />
                </span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</div>
                  <div className="font-display text-lg font-bold tracking-tight text-slate-900">
                    2118 Glenridge Ct
                  </div>
                  <div className="text-sm text-slate-600">Marietta, GA 30062 · United States</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-6 overflow-hidden border border-slate-200 bg-white">
            <iframe
              title="Map"
              className="h-64 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=2118+Glenridge+Ct,+Marietta,+GA+30062&output=embed"
            />
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          {sent ? (
            <div className="border-2 border-emerald-500 bg-white p-8" data-testid="contact-sent">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
                Message received
              </div>
              <h2 className="mt-2 font-display text-3xl font-black tracking-tighter text-slate-900">
                Thanks, {form.name.split(" ")[0] || "friend"}!
              </h2>
              <p className="mt-2 text-slate-600">
                We&apos;ll reply to {form.email} within an hour during business hours.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-slate-200 bg-white p-6"
            >
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Send a message
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Full name *" id="c-name">
                  <Input id="c-name" data-testid="contact-name" value={form.name} onChange={update("name")} required />
                </Field>
                <Field label="Email *" id="c-email">
                  <Input id="c-email" data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} required />
                </Field>
                <Field label="Phone (optional)" id="c-phone">
                  <Input id="c-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={update("phone")} />
                </Field>
                <Field label="Subject" id="c-subject">
                  <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={update("subject")} placeholder="e.g. Refund request" />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Message *" id="c-message">
                  <Textarea
                    id="c-message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    placeholder="How can we help?"
                    required
                  />
                </Field>
              </div>
              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="mt-6 rounded-sm bg-slate-900 px-6 py-5 font-bold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? "Sending…" : (
                  <>
                    <Send className="mr-1 h-4 w-4" /> Send message
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-wider">
      {label}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default Contact;
